import {useEffect, useState, useRef} from 'react'
import {gsap} from 'gsap'

import ButterflyComponent from './Butterfly'

const ROTATEZ = 145

const CtaButterfly = () => {
  const butterflyRef = useRef<HTMLDivElement>(null)
  const [pearlTrigger, setPearlTrigger] = useState(false)
  const [wingIntensity, setWingIntensity] = useState(1)
  const [wingSpeedRatio, setWingSpeedRatio] = useState(1)

  const animTrigger = useRef(false)
  const animCount = useRef(0)

  const clamp = (inStart: number, inEnd: number, outStart: number, outEnd: number, inValue: number): number => {
    const value = gsap.utils.mapRange(inStart, inEnd, outStart, outEnd, inValue)
    return Math.max(Math.min(outStart, outEnd), Math.min(Math.max(outStart, outEnd), value))
  }

  useEffect(() => {
    const butterfly = butterflyRef.current
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (butterfly) {
      if (mediaQuery.matches) {
        gsap.set(butterfly, {opacity: 1, rotateZ: ROTATEZ})
      } else {
        let isInit = false
        const tl = gsap.timeline({
          delay: 1,
          paused: true,
          onComplete() {
            animTrigger.current = false
          },
          onUpdate() {
            // floating
            const _progress = this.progress()
            const p = gsap.parseEase('power4.out')(_progress)

            setWingIntensity(() => {
              return clamp(0.8, 0.9, 1, 0, _progress)
            }) // Adjust wing intensity based on progress

            setWingSpeedRatio(() => {
              return clamp(0.4, 1.0, 1.5, 0.2, _progress)
            })

            if (_progress > 0.8) {
              setPearlTrigger(false)
            }

            const x = 1500 * (1.0 - p) + Math.sin(p * Math.PI * 2) * 50
            const y = -200 * (1.0 - p) + Math.sin(p * Math.PI * 2) * (20 + 130 * (1 - _progress))
            gsap.set(butterfly, {
              x: `${x}%`,
              y: `${y}%`,
            })
          },
        })

        // rotating while floating
        tl.set(butterfly, {opacity: 1})
          .fromTo(
            butterfly,
            {
              rotationZ: ROTATEZ,
              rotationX: -40,
            },
            {
              rotationZ: ROTATEZ,
              rotationX: 40,
              rotationY: 20,
              duration: 1,
            },
          )
          .to(butterfly, {
            rotationZ: ROTATEZ,
            rotationX: -40,
            rotationY: 0,
            duration: 1,
          })
          .to(butterfly, {
            rotationZ: ROTATEZ,
            rotationX: 0,
            duration: 3,
            ease: 'power2.out',
          })

        // Intersection Observer to trigger animation
        const observer = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                if (!isInit) {
                  setPearlTrigger(true)
                  animTrigger.current = true
                  tl.play() // Play the animation when in viewport
                  isInit = true
                }
                // If already initialized, check for pearl trigger and start the flying animation
                else {
                  if (animTrigger.current) {
                    break
                  } else {
                    animCount.current += 1
                    setPearlTrigger(true)
                    animTrigger.current = true
                    const sign = animCount.current % 2 === 0 ? -1 : 1
                    const randomStartRadian = (Math.random() * 0.2 + 0.5) * Math.PI * sign
                    gsap
                      .timeline({
                        onComplete() {
                          animTrigger.current = false // Reset trigger after animation completes
                        },
                        onUpdate() {
                          const _progress = this.progress()

                          setWingIntensity(() => {
                            return clamp(0.8, 0.9, 0.8, 0, _progress)
                          }) // Adjust wing intensity based on progress

                          setWingSpeedRatio(() => {
                            return clamp(0.4, 1.0, 1.5, 0.2, _progress)
                          })

                          if (_progress > 0.9) {
                            setPearlTrigger(false)
                          }

                          const p = gsap.parseEase('power3.out')(_progress)
                          // const parabola = 4 * _progress * (1 - _progress)
                          const radius = 200 * (1 - p)
                          const x = -Math.cos(sign * p * Math.PI * 1 + randomStartRadian) * radius * 1
                          const y = p * Math.sin(sign * p * Math.PI * 3 + randomStartRadian) * radius * 0.3
                          gsap.set(butterfly, {
                            x: `${x}%`,
                            y: `${y}%`,
                          })
                        },
                      }) // X rotation
                      .fromTo(
                        butterfly,
                        {
                          rotationX: -20 * sign,
                        },
                        {
                          rotationX: 0,
                          duration: 3,
                        },
                        0,
                      ) // Y rotation
                      .fromTo(
                        butterfly,
                        {
                          rotationY: 20,
                        },
                        {
                          rotationY: 0,
                          duration: 3,
                        },
                        0,
                      ) // Z roatation
                      .fromTo(
                        butterfly,
                        {
                          rotationZ: ROTATEZ + (sign > 0 ? -10 : 10),
                        },
                        {
                          rotationZ: ROTATEZ + (sign > 0 ? 10 : -10),
                          duration: 1,
                        },
                        0,
                      )
                      .to(
                        butterfly,
                        {
                          rotationZ: ROTATEZ,
                          ease: 'power2.inOut',
                          duration: 1,
                        },
                        1,
                      )
                      .fromTo(
                        butterfly,
                        {
                          scale: 0.5,
                        },
                        {
                          scale: 1,
                          duration: 3,
                          ease: 'power2.out',
                        },
                        0,
                      )
                  }
                }

                break
              }
            }
          },
          {threshold: 0.5}, // Trigger when 50% of the component is visible
        )

        observer.observe(butterfly)

        // Cleanup event listeners on unmount
        return () => {
          observer.disconnect()
        }
      }
    }
  }, [])

  return (
    <div className="butterflyContainer">
      <div className="ctaButterfly" ref={butterflyRef}>
        <ButterflyComponent
          DefaultColorClass={'defaultWhiteColor'}
          pearlTrigger={pearlTrigger}
          wingIntensity={wingIntensity}
          wingSpeedRatio={wingSpeedRatio}
        />
      </div>
    </div>
  )
}

export default CtaButterfly

try{ CtaButterfly.displayName ||= 'CtaButterfly' } catch {}